import React, { useState } from 'react';
import { UpButtonClear } from '../Line/UpButton';
import { FlatTree, Level } from '../../models/api/codegen/code';
import { LiveOrLineType } from '../../store/spirs/types';
import { TreeInit } from '../../store/spirs/reducers/treeReducer';
import { buildVisibleRows } from './rowBuilder';
import { RowComponent, RowProps } from './TreeRow';
import { List, useDynamicRowHeight, useListRef  } from 'react-window';

export type SportsListProps = {
    mode: LiveOrLineType;
    tree: FlatTree;
    sportId?: number;
    fullHeight: boolean;
}

export const TreeDirectory = React.memo((props: SportsListProps) => {
    const { mode, tree, fullHeight } = props;

    const listRefV = useListRef(null);
    const [visibleLevels, setVisibleLevels] = useState<Level[]>([]);
    const [localTree, setLocalTree] = useState(TreeInit);
    const [expandedSports, setExpandedSports] = useState<Set<number>>(() => new Set());
    const [height, setHeight] = useState<number>(window.innerHeight);
    const [minShowIndex, setMinShowIndex] = React.useState(0);
    const rowHeight = useDynamicRowHeight({ defaultRowHeight: 50 });

    React.useEffect(() => {
        const next = buildVisibleRows(tree.l, expandedSports, props.sportId);
        setVisibleLevels(next);
        setLocalTree(tree);
    }, [tree.l, tree.v, expandedSports, props.sportId]);

    React.useEffect(() => {
        const onResize = () => setHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const onRowsRendered = (visibleRows: { startIndex: number; stopIndex: number; }, allRows: { startIndex: number; stopIndex: number; }) => {
        setMinShowIndex(visibleRows.startIndex)
    }

    const onClick = () => {
        const list = listRefV.current;
        list?.scrollToRow({
            align: "auto", // optional
            behavior: "auto", // optional
            index: 0
        });
    };

    const toggleSport = React.useCallback((sportId: number) => {
        setExpandedSports(prev => {
            const next = new Set(prev);
            next.has(sportId) ? next.delete(sportId) : next.add(sportId);
            return next;
        });
    }, []);

    return (
        <div className="w-full">
            <List<RowProps>
                onRowsRendered={onRowsRendered}
                style={{height: fullHeight ? '100%' : height - 150}}
                listRef={listRefV}
                rowComponent={RowComponent}
                rowCount={visibleLevels.length}
                rowHeight={rowHeight}
                rowProps={{visibleLevels, toggleSport, tree: localTree, mode}}
            />
            {<UpButtonClear visible={minShowIndex > 0} onClick={onClick}/>}
        </div>
    );
}, (prevProps, nextProps) => prevProps.tree.l === nextProps.tree.l && prevProps.sportId === nextProps.sportId);
